import type { Story, StoryStatus } from '../api/types.js';
import { Card } from './ui.js';
import { StoryStatusBadge } from '../status.js';

/** The board's lanes, one per story status, in workflow order. */
const LANES: StoryStatus[] = [
  'draft',
  'in_review',
  'ready',
  'approved',
  'building',
  'built',
  'accepted',
];

/** Compact hours label, e.g. `6h` or `6.5h`. */
function hoursLabel(hours: number): string {
  const rounded = Number.isInteger(hours) ? hours : Number(hours.toFixed(1));
  return `${rounded}h`;
}

/**
 * A read-only Kanban view of a project's stories, one lane per status. Each lane
 * header sums the equivalent developer hours of its stories — the hours they took
 * to build, or are anticipated to take if not built yet. Shared by the console
 * and the founder view; the stories arrive already ordered by sequence.
 */
export function StoryBoard({ stories }: { stories: Story[] }) {
  const byStatus = new Map<StoryStatus, Story[]>(LANES.map((lane) => [lane, []]));
  for (const story of stories) byStatus.get(story.status)?.push(story);

  if (stories.length === 0) {
    return (
      <Card>
        <h2 className="mb-2 font-title text-2xl tracking-wide">Board</h2>
        <p className="opacity-70">
          The board fills in once your specification is split into user stories.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="mb-3 font-title text-2xl tracking-wide">Board</h2>
      <div className="overflow-x-auto pb-2">
        <div className="flex min-w-max gap-3">
          {LANES.map((lane) => {
            const items = byStatus.get(lane) ?? [];
            const total = items.reduce((sum, s) => sum + (s.equivalentDevHours ?? 0), 0);
            return (
              <div
                key={lane}
                className="flex w-64 flex-shrink-0 flex-col rounded-lg bg-neutral/40 p-2"
              >
                <div className="mb-2 flex items-center justify-between gap-2 px-1">
                  <StoryStatusBadge status={lane} />
                  <span className="text-xs tabular-nums opacity-60">
                    {items.length} · {hoursLabel(total)}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {items.map((s) => (
                    <div
                      key={s.id}
                      className="rounded-md bg-white p-3 text-sm shadow-sm ring-1 ring-secondary/5"
                    >
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs opacity-40">#{s.sequence}</span>
                        <span className="font-medium leading-snug">{s.title}</span>
                      </div>
                      {s.epic ? <div className="mt-1 text-xs opacity-50">{s.epic}</div> : null}
                      {s.equivalentDevHours !== null ? (
                        <div className="mt-1 text-xs font-medium text-primary">
                          ≈ {hoursLabel(s.equivalentDevHours)}
                        </div>
                      ) : null}
                    </div>
                  ))}
                  {items.length === 0 ? (
                    <div className="rounded-md border border-dashed border-secondary/15 p-3 text-center text-xs opacity-40">
                      None
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
